import React, { useState } from "react";
import { Button } from "../ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../ui/alert-dialog";
import { X, Trash, Lightning, Check } from "@phosphor-icons/react";
import { OVERALL_STATUSES, DEV_STATUSES, PRIORITIES, RISK_LEVELS } from "../../lib/constants";

const FIELD_OPTIONS = [
  { key: "overall_status", label: "Overall Status", options: OVERALL_STATUSES },
  { key: "development_status", label: "Dev Status", options: DEV_STATUSES },
  { key: "priority", label: "Priority", options: PRIORITIES },
  { key: "risk_level", label: "Risk Level", options: RISK_LEVELS },
];

export default function BulkActionBar({
  selectedCount, totalMatching, allSelected,
  onSelectAllAcrossPages, onClear,
  canEdit, canDelete,
  onBulkUpdate, onBulkDelete,
}) {
  const [field, setField] = useState("overall_status");
  const [value, setValue] = useState("");
  const [applying, setApplying] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const currentField = FIELD_OPTIONS.find((f) => f.key === field) || FIELD_OPTIONS[0];

  if (selectedCount === 0) return null;

  const doUpdate = async () => {
    if (!value) return;
    setApplying(true);
    try {
      await onBulkUpdate({ [field]: value });
      setValue("");
    } finally {
      setApplying(false);
    }
  };

  const doDelete = async () => {
    setApplying(true);
    try {
      await onBulkDelete();
      setConfirmDelete(false);
    } finally {
      setApplying(false);
    }
  };

  return (
    <>
    <div
      className="flex items-center gap-3 bg-[#387EE6] text-white rounded-lg px-4 py-3 border border-[#2563EB]"
      data-testid="bulk-action-bar"
    >
      <span
        className="text-sm font-semibold flex items-center gap-2"
        data-testid="bulk-selected-count"
      >
        <Check size={16} weight="bold" />
        {selectedCount} selected
      </span>

      {!allSelected && totalMatching > selectedCount ? (
        <button
          type="button"
          onClick={onSelectAllAcrossPages}
          data-testid="bulk-select-all-btn"
          className="text-xs text-white/80 hover:text-white underline underline-offset-2"
        >
          Select all {totalMatching} matching
        </button>
      ) : null}

      <span className="text-xs text-white/40">·</span>

      {canEdit ? (
        <div className="flex items-center gap-2">
          <Lightning size={14} weight="bold" className="text-white/60" />
          <Select value={field} onValueChange={(v) => { setField(v); setValue(""); }}>
            <SelectTrigger
              data-testid="bulk-field-select"
              className="h-8 w-[150px] bg-neutral-800 border-neutral-700 text-white text-xs"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {FIELD_OPTIONS.map((f) => (
                <SelectItem key={f.key} value={f.key}>{f.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <span className="text-white/60 text-xs">→</span>
          <Select value={value} onValueChange={setValue}>
            <SelectTrigger
              data-testid="bulk-value-select"
              className="h-8 w-[160px] bg-neutral-800 border-neutral-700 text-white text-xs"
            >
              <SelectValue placeholder="Choose value..." />
            </SelectTrigger>
            <SelectContent>
              {currentField.options.map((o) => (
                <SelectItem key={o} value={o}>{o}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            size="sm"
            variant="secondary"
            disabled={!value || applying}
            onClick={doUpdate}
            data-testid="bulk-apply-btn"
            className="h-8 bg-white text-neutral-900 hover:bg-neutral-100"
          >
            {applying ? "Applying…" : "Apply"}
          </Button>
        </div>
      ) : null}

      <div className="flex-1" />

      {canDelete ? (
        <Button
          size="sm"
          variant="destructive"
          onClick={() => setConfirmDelete(true)}
          data-testid="bulk-delete-btn"
          className="h-8 bg-red-600 hover:bg-red-700 text-white"
        >
          <Trash size={14} weight="bold" className="mr-1.5" />
          Delete
        </Button>
      ) : null}

      <button
        type="button"
        onClick={onClear}
        data-testid="bulk-clear-btn"
        className="h-8 w-8 flex items-center justify-center rounded hover:bg-white/10 text-white/70"
        aria-label="Clear selection"
      >
        <X size={16} weight="bold" />
      </button>
    </div>

    <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
      <AlertDialogContent data-testid="bulk-delete-confirm-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>Delete {selectedCount} task{selectedCount === 1 ? "" : "s"}?</AlertDialogTitle>
          <AlertDialogDescription>
            This will permanently remove all selected tasks. Their audit history will remain accessible in the Activity Log.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="bulk-delete-cancel-btn">Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={doDelete}
            data-testid="bulk-delete-confirm-btn"
            className="bg-red-600 hover:bg-red-700"
          >
            Delete {selectedCount}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
}
